/**
 * Class Codes & Student Settings Tab - כיתות ותלמידים
 * Admin management of class enrollment codes + the two student settings
 * (per-student spend cap, max total student accounts).
 */

import { useState, useEffect, FormEvent } from 'react'
import { GraduationCap, DollarSign, Users, Plus, Check, AlertCircle, RefreshCw, Power, Trash2 } from 'lucide-react'
import { useAdminStore } from '../../store/adminStore'

export default function ClassCodesTab() {
  const {
    studentSettings, studentSettingsLoading, studentSettingsError, fetchStudentSettings, setStudentSettings,
    classCodes, classCodesLoading, classCodesError, fetchClassCodes, createClassCode, toggleClassCode, deleteClassCode,
  } = useAdminStore()

  const [capInput, setCapInput] = useState('')
  const [maxInput, setMaxInput] = useState('')
  const [settingsSaved, setSettingsSaved] = useState(false)

  const [newCode, setNewCode] = useState('')
  const [newLabel, setNewLabel] = useState('')
  const [newSeats, setNewSeats] = useState('30')
  const [createError, setCreateError] = useState<string | null>(null)

  useEffect(() => {
    fetchStudentSettings()
    fetchClassCodes()
  }, [fetchStudentSettings, fetchClassCodes])

  useEffect(() => {
    if (studentSettings) {
      setCapInput(String(studentSettings.studentSpendCapUSD))
      setMaxInput(String(studentSettings.maxStudentAccounts))
    }
  }, [studentSettings])

  const settingsDirty =
    studentSettings &&
    (parseFloat(capInput) !== studentSettings.studentSpendCapUSD ||
      parseInt(maxInput, 10) !== studentSettings.maxStudentAccounts)

  const handleSaveSettings = async () => {
    const cap = parseFloat(capInput)
    const max = parseInt(maxInput, 10)
    if (!Number.isFinite(cap) || cap < 0 || cap > 100) return
    if (!Number.isInteger(max) || max < 0 || max > 1000000) return
    try {
      await setStudentSettings({ studentSpendCapUSD: cap, maxStudentAccounts: max })
      setSettingsSaved(true)
      setTimeout(() => setSettingsSaved(false), 3000)
    } catch { /* error shown from store */ }
  }

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault()
    setCreateError(null)
    const seats = parseInt(newSeats, 10)
    if (!/^[A-Za-z0-9-]{3,32}$/.test(newCode.trim())) {
      setCreateError('קוד כיתה לא תקין (3-32 תווים: אותיות באנגלית, ספרות, מקף)')
      return
    }
    if (newLabel.trim().length < 2) { setCreateError('נדרש שם כיתה/בית ספר'); return }
    if (!Number.isInteger(seats) || seats < 1) { setCreateError('מספר מקומות לא תקין'); return }
    try {
      await createClassCode({ code: newCode.trim().toUpperCase(), label: newLabel.trim(), seatLimit: seats })
      setNewCode(''); setNewLabel(''); setNewSeats('30')
    } catch (err: any) {
      setCreateError(err?.response?.data?.error || 'שגיאה ביצירת קוד כיתה')
    }
  }

  return (
    <div className="space-y-6">
      {/* ─── Student settings card ─── */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <GraduationCap className="text-primary-600" size={24} aria-hidden="true" />
          <h2 className="text-xl font-bold text-gray-900">הגדרות התלמידים</h2>
        </div>

        {studentSettingsError && (
          <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm mb-4" role="alert">{studentSettingsError}</div>
        )}
        {settingsSaved && (
          <div className="bg-green-50 text-green-700 p-3 rounded-lg text-sm mb-4 flex items-center gap-2">
            <Check size={16} aria-hidden="true" /> ההגדרות נשמרו בהצלחה
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="capInput" className="block text-sm font-medium text-gray-700 mb-2">
              מכסת שימוש לתלמיד (דולר, לכל חשבון)
            </label>
            <div className="relative">
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"><DollarSign size={16} /></span>
              <input
                id="capInput" type="number" min={0} max={100} step={0.5}
                value={capInput} onChange={(e) => setCapInput(e.target.value)}
                className="input pr-9" dir="ltr"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">כשתלמיד מגיע למכסה, השיחות נחסמות עד שמנהל מאפס אותה.</p>
          </div>

          <div>
            <label htmlFor="maxInput" className="block text-sm font-medium text-gray-700 mb-2">
              מספר חשבונות תלמידים מקסימלי
            </label>
            <div className="relative">
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"><Users size={16} /></span>
              <input
                id="maxInput" type="number" min={0} max={1000000} step={1}
                value={maxInput} onChange={(e) => setMaxInput(e.target.value)}
                className="input pr-9" dir="ltr"
              />
            </div>
            {studentSettings?.currentStudentCount !== undefined && (
              <p className="text-xs text-gray-500 mt-1">רשומים כרגע: {studentSettings.currentStudentCount}</p>
            )}
          </div>
        </div>

        <button
          onClick={handleSaveSettings}
          disabled={studentSettingsLoading || !settingsDirty}
          className="btn btn-primary mt-4 disabled:opacity-50"
        >
          שמירת הגדרות
        </button>
      </div>

      {/* ─── Create class code ─── */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">יצירת קוד כיתה</h2>
        {createError && (
          <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm mb-4 flex items-center gap-2" role="alert">
            <AlertCircle size={16} aria-hidden="true" /> {createError}
          </div>
        )}
        <form onSubmit={handleCreate} className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div>
            <label htmlFor="newCode" className="block text-sm font-medium text-gray-700 mb-1">קוד</label>
            <input id="newCode" value={newCode} onChange={(e) => setNewCode(e.target.value)} className="input" dir="ltr" placeholder="SCHOOL-9A" />
          </div>
          <div>
            <label htmlFor="newLabel" className="block text-sm font-medium text-gray-700 mb-1">שם כיתה / בית ספר</label>
            <input id="newLabel" value={newLabel} onChange={(e) => setNewLabel(e.target.value)} className="input" placeholder="תיכון X - כיתה ט'1" />
          </div>
          <div>
            <label htmlFor="newSeats" className="block text-sm font-medium text-gray-700 mb-1">מספר מקומות</label>
            <input id="newSeats" type="number" min={1} value={newSeats} onChange={(e) => setNewSeats(e.target.value)} className="input" dir="ltr" />
          </div>
          <button type="submit" className="btn btn-primary flex items-center justify-center gap-2">
            <Plus size={18} aria-hidden="true" /> צור
          </button>
        </form>
      </div>

      {/* ─── Class codes list ─── */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900">קודי כיתות</h2>
          <button onClick={() => fetchClassCodes()} disabled={classCodesLoading} className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700">
            <RefreshCw size={16} className={classCodesLoading ? 'animate-spin' : ''} aria-hidden="true" /> רענן
          </button>
        </div>
        {classCodesError && (
          <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm mb-4" role="alert">{classCodesError}</div>
        )}
        {classCodes.length === 0 ? (
          <p className="text-gray-500 text-sm">עדיין אין קודי כיתות.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-right text-gray-500 border-b">
                  <th className="py-2 px-2">קוד</th>
                  <th className="py-2 px-2">כיתה</th>
                  <th className="py-2 px-2">תפוסה</th>
                  <th className="py-2 px-2">סטטוס</th>
                  <th className="py-2 px-2">פעולות</th>
                </tr>
              </thead>
              <tbody>
                {classCodes.map((c) => (
                  <tr key={c._id} className="border-b last:border-0">
                    <td className="py-2 px-2 font-mono" dir="ltr">{c.code}</td>
                    <td className="py-2 px-2">{c.label}</td>
                    <td className="py-2 px-2">{c.seatsUsed}/{c.seatLimit}</td>
                    <td className="py-2 px-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs ${c.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        {c.isActive ? 'פעיל' : 'מושבת'}
                      </span>
                    </td>
                    <td className="py-2 px-2">
                      <div className="flex items-center gap-2">
                        <button onClick={() => toggleClassCode(c._id)} className="text-gray-500 hover:text-primary-600" title={c.isActive ? 'השבת' : 'הפעל'}>
                          <Power size={16} aria-hidden="true" />
                        </button>
                        {c.seatsUsed === 0 && (
                          <button onClick={() => { if (confirm(`למחוק את הקוד ${c.code}?`)) deleteClassCode(c._id) }} className="text-gray-500 hover:text-red-600" title="מחק">
                            <Trash2 size={16} aria-hidden="true" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
